import { CELL, FLORA_SALT, GRID_SIZE } from '../game/constants';
import { dayNumber } from '../game/daily';
import { key } from '../game/geometry';
import { mulberry32, randInt } from '../game/rng';
import type { Puzzle } from '../game/types';
import { GRASS_VARIANTS, PEBBLE_VARIANTS, SPRITE } from './sprites';
import { el } from './svg';

/*
 * Ground flora — terrain art, not ambient life (STYLEGUIDE §4).
 *
 * A sparse seeded scatter of grass tufts, water's-edge reeds, pebbles and
 * scrub across the land cells. Always visible, never animated, and never a
 * fill: toned fill is the blocked-terrain signal, so flora is linework only
 * and the flat land field stays untouched. Drawn once into #terrain at
 * mount, beneath every placed object and outside the per-move render path,
 * so it can never react to play — the anti-prospecting rule (§8 item 6)
 * holds by construction.
 */

/** Chance per land cell of each ground piece; one piece per cell at most.
    The ladder reads cumulatively on one roll: tree, bush, scrub, boulder,
    pebble, grass. */
const P_TREE = 0.05;
const P_BUSH = 0.09;
const P_SCRUB = 0.13;
const P_BOULDER = 0.17;
const P_PEBBLE = 0.28;
const P_GRASS = 0.58;

/** How far a piece may sit off its cell centre, in board units. Pieces may
    brush or cross a cell edge — maps have drift, scatters don't tile (§4). */
const JITTER = 34;

/** The big props rein in beside water so a bush or boulder never stands
    inside a shoreline bay arc (COVE_R = 24 board units into the cell). */
const PROP_JITTER_NEAR_WATER = 20;

/** Chance of a reed cluster per land–water edge, capped per cell. */
const P_REED = 0.55;
const REEDS_MAX = 2;

/** N, E, S, W — fixed order, so the per-cell stream replays identically. */
const EDGES = [
  [-1, 0],
  [0, 1],
  [1, 0],
  [0, -1],
] as const;

const round1 = (v: number): number => Math.round(v * 10) / 10;

function place(g: SVGGElement, href: string, x: number, y: number, flip: number, rot: number) {
  const transform =
    `translate(${round1(x)} ${round1(y)})` +
    (rot !== 0 ? ` rotate(${round1(rot)})` : '') +
    (flip < 0 ? ' scale(-1,1)' : '');
  el('use', { href, class: 'flora', transform }, g);
}

/** Lay the day's flora over every land cell of the puzzle. */
export function drawFlora(g: SVGGElement, puzzle: Puzzle): void {
  const day = dayNumber();
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (puzzle.obstacles[key({ r, c })]) continue;
      // Per-cell stream, so placement is stable whatever order cells are
      // visited in — the water-glint convention.
      const rnd = mulberry32(day ^ FLORA_SALT ^ (r * 97 + c * 31));
      // Draw every roll up front, then decide: a branch that skipped a roll
      // would reshuffle the whole field the next time anyone edits this.
      const type = rnd();
      const jx = (rnd() * 2 - 1) * JITTER;
      const jy = (rnd() * 2 - 1) * JITTER;
      const grassV = randInt(rnd, GRASS_VARIANTS);
      const pebbleV = randInt(rnd, PEBBLE_VARIANTS);
      const flip = rnd() < 0.5 ? -1 : 1;
      const lean = rnd() * 2 - 1;
      const x = c * CELL + 50 + jx;
      const y = r * CELL + 50 + jy;

      // A big prop peeking from under a silhouette reads as collision, so
      // the plant and house cells keep to the quiet pieces; a grass blade
      // or a pebble reads as a yard.
      const structure =
        (r === puzzle.plant.r && c === puzzle.plant.c) ||
        puzzle.houses.some((h) => h.r === r && h.c === c);
      let nearWater = false;
      for (const dr of [-1, 0, 1]) {
        for (const dc of [-1, 0, 1]) {
          if (puzzle.obstacles[key({ r: r + dr, c: c + dc })] === 'water') nearWater = true;
        }
      }
      const propK = nearWater ? PROP_JITTER_NEAR_WATER / JITTER : 1;
      const px = c * CELL + 50 + jx * propK;
      const py = r * CELL + 50 + jy * propK;

      if (type < P_BOULDER && !structure) {
        if (type < P_TREE) place(g, SPRITE.tree, px, py, flip, lean * 2);
        else if (type < P_BUSH) place(g, SPRITE.bush, px, py, flip, lean * 3);
        else if (type < P_SCRUB) place(g, SPRITE.scrub, px, py, flip, lean * 4);
        else place(g, SPRITE.boulder, px, py, flip, 0);
      } else if (type < P_PEBBLE) place(g, SPRITE.pebble + pebbleV, x, y, flip, 0);
      else if (type < P_GRASS) place(g, SPRITE.grass + grassV, x, y, flip, lean * 7);

      // Reeds stand at the water's edge, in addition to the cell's own
      // piece: base a few units inside the land cell so the stems straddle
      // the shoreline arcs. Rolls are consumed for every water edge whether
      // or not a reed lands, for the same replay discipline as above.
      let reeds = 0;
      for (const [dr, dc] of EDGES) {
        const nr = r + dr;
        const nc = c + dc;
        if (nr < 0 || nr >= GRID_SIZE || nc < 0 || nc >= GRID_SIZE) continue;
        if (puzzle.obstacles[key({ r: nr, c: nc })] !== 'water') continue;
        const chance = rnd();
        const along = (rnd() * 2 - 1) * 30;
        const inset = 4 + rnd() * 4;
        const rflip = rnd() < 0.5 ? -1 : 1;
        if (chance >= P_REED || reeds >= REEDS_MAX) continue;
        reeds++;
        const rx = dc === 0 ? c * CELL + 50 + along : c * CELL + 50 + dc * (50 - inset);
        const ry = dr === 0 ? r * CELL + 50 + along : r * CELL + 50 + dr * (50 - inset);
        place(g, SPRITE.reed, rx, ry, rflip, 0);
      }
    }
  }
}
